/**
 * The one HTTP client for the Lumina API.
 *
 * - Real requests only. When the server is unreachable or returns an error the caller gets a typed
 *   `ApiError` (with the server's stable `code`) and the screen shows it.
 * - Bearer token comes from the auth layer through `configureApi`; components never touch it.
 * - A 401 on a protected call triggers ONE single-flight refresh-token rotation shared by every
 *   concurrent request, then the original request is retried once with the new access token.
 *   If refresh is impossible the auth layer is told (`onAuthFailure`) and the 401 is surfaced.
 * - Callers can abort with an AbortSignal (TanStack Query passes one automatically).
 * - The demo build (`VITE_APP_MODE=demo`) answers every call in the browser from `src/demo/` instead of the network;
 *   the reply goes through the same status/error handling below, so screens behave exactly as against a real server.
 */
import { env, IS_DEMO } from '../env';

const REQUEST_TIMEOUT_MS = 30_000;

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];
export type QueryParams = Record<string, QueryValue>;

export interface RequestOptions {
  query?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** How to read a successful response. Default: json (204 and empty bodies give undefined). */
  responseType?: 'json' | 'blob' | 'text';
  /** Skip the Authorization header and the refresh-on-401 behaviour (login, refresh, public OPAC calls). */
  anonymous?: boolean;
  /** Sent as `Idempotency-Key` so a retried money request cannot be applied twice. */
  idempotencyKey?: string;
  timeoutMs?: number;
}

export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'validation'
  | 'conflict'
  | 'server'
  | 'unknown';

export class ApiError extends Error {
  readonly status: number;
  /** Stable machine-readable domain code from the API (e.g. ITEM_NOT_FOUND), when provided. */
  readonly code: string | undefined;
  readonly kind: ApiErrorKind;
  /** Field-level or per-rule messages returned by server validation. */
  readonly details: string[];
  /** Raw parsed error body for the rare caller that needs extra fields. */
  readonly body: unknown;

  constructor(message: string, status: number, kind: ApiErrorKind, options: { code?: string; details?: string[]; body?: unknown } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.kind = kind;
    this.code = options.code;
    this.details = options.details ?? [];
    this.body = options.body;
  }

  get isRetryable(): boolean {
    return this.kind === 'network' || this.kind === 'timeout' || this.kind === 'server';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export interface ApiHandlers {
  /** Current access token (held in memory by the auth layer). */
  getAccessToken: () => string | null;
  /**
   * Rotate the refresh token and resolve the new access token, or null when the session is over
   * (no/invalid refresh token). May throw on transient failures: the session is then kept.
   */
  refresh: (() => Promise<string | null>) | null;
  /** The session is definitively gone: the auth layer clears state and sends the user to sign in. */
  onAuthFailure: () => void;
}

let handlers: ApiHandlers = { getAccessToken: () => null, refresh: null, onAuthFailure: () => undefined };
let refreshing: Promise<string | null> | null = null;

export function configureApi(next: Partial<ApiHandlers>): void {
  handlers = { ...handlers, ...next };
}

/**
 * Refresh the access token, sharing one in-flight rotation between every caller (a burst of
 * parallel 401s, or the bootstrap in React StrictMode, must not spend a one-time refresh token twice).
 */
export function refreshAccessToken(): Promise<string | null> {
  const refresh = handlers.refresh;
  if (!refresh) {
    return Promise.resolve(null);
  }
  refreshing ??= refresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export function apiBaseUrl(): string {
  return env.apiUrl;
}

export function buildUrl(path: string, query?: QueryParams): string {
  const url = `${apiBaseUrl()}/${path.replace(/^\/+/, '')}`;
  if (!query) {
    return url;
  }
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        search.append(key, String(item));
      }
    } else {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) {
    return 'unauthorized';
  }
  if (status === 403) {
    return 'forbidden';
  }
  if (status === 404) {
    return 'not_found';
  }
  if (status === 409) {
    return 'conflict';
  }
  if (status === 400 || status === 422) {
    return 'validation';
  }
  return status >= 500 ? 'server' : 'unknown';
}

/** Nest returns `{ statusCode, message: string | string[], error?, code? }`. */
export function readErrorBody(payload: unknown, fallback: string): { message: string; details: string[]; code?: string } {
  if (payload && typeof payload === 'object') {
    const record = payload as { message?: unknown; code?: unknown };
    const code = typeof record.code === 'string' ? record.code : undefined;
    if (Array.isArray(record.message)) {
      const details = record.message.filter((item): item is string => typeof item === 'string');
      return { message: details[0] ?? fallback, details, code };
    }
    if (typeof record.message === 'string' && record.message.trim()) {
      return { message: record.message, details: [record.message], code };
    }
    return { message: fallback, details: [], code };
  }
  return { message: fallback, details: [] };
}

async function parseBody(response: Response, responseType: 'json' | 'blob' | 'text', ok: boolean): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }
  if (ok && responseType === 'blob') {
    return response.blob();
  }
  const text = await response.text();
  if (!text) {
    return undefined;
  }
  if (ok && responseType === 'text') {
    return text;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return ok ? text : undefined;
  }
}

/** What came back, whether from the network or from the in-browser demo engine. */
interface RawReply {
  status: number;
  ok: boolean;
  statusText: string;
  /** Read lazily: a 401 that triggers a token refresh is retried without ever consuming its body. */
  payload: () => Promise<unknown>;
}

async function demoAttempt(method: string, path: string, options: RequestOptions, token: string | null): Promise<RawReply> {
  const { handleDemoRequest } = await import('@/demo');
  const reply = await handleDemoRequest({ method, path, query: options.query, body: options.body, token: options.anonymous ? null : token, signal: options.signal });
  const ok = reply.status >= 200 && reply.status < 300;
  return { status: reply.status, ok, statusText: '', payload: () => Promise.resolve(reply.body) };
}

async function attempt(method: string, path: string, options: RequestOptions, token: string | null): Promise<RawReply> {
  if (IS_DEMO) {
    return demoAttempt(method, path, options, token);
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), options.timeoutMs ?? REQUEST_TIMEOUT_MS);
  const onCallerAbort = (): void => controller.abort(options.signal?.reason);
  if (options.signal?.aborted) {
    controller.abort(options.signal.reason);
  }
  options.signal?.addEventListener('abort', onCallerAbort, { once: true });

  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  if (token && !options.anonymous) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (options.idempotencyKey) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }
  const isForm = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const hasBody = options.body !== undefined;
  if (hasBody && !isForm) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    const response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body: !hasBody ? undefined : isForm ? (options.body as FormData) : JSON.stringify(options.body),
      signal: controller.signal,
    });
    return { status: response.status, ok: response.ok, statusText: response.statusText, payload: () => parseBody(response, options.responseType ?? 'json', response.ok) };
  } catch (error) {
    if (options.signal?.aborted) {
      throw error;
    }
    if (controller.signal.aborted) {
      throw new ApiError('The server took too long to respond. Please try again.', 0, 'timeout');
    }
    throw new ApiError('Cannot reach the Lumina server. Check your connection and try again.', 0, 'network');
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener('abort', onCallerAbort);
  }
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  let response = await attempt(method, path, options, handlers.getAccessToken());

  if (response.status === 401 && !options.anonymous && handlers.refresh) {
    let fresh: string | null;
    try {
      fresh = await refreshAccessToken();
    } catch {
      // Transient refresh failure (offline, 5xx): keep the session, surface the original 401.
      fresh = undefined as unknown as string | null;
    }
    if (fresh) {
      response = await attempt(method, path, options, fresh);
    } else if (fresh === null) {
      handlers.onAuthFailure();
    }
  }

  const payload = await response.payload();
  if (!response.ok) {
    const { message, details, code } = readErrorBody(payload, response.statusText || 'Request failed');
    throw new ApiError(message, response.status, kindForStatus(response.status), { code, details, body: payload });
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body'>) => request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) => request<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) => request<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) => request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'body'>) => request<T>('DELETE', path, options),
  /** multipart upload: pass a FormData (do not set Content-Type yourself). */
  upload: <T>(path: string, form: FormData, options?: Omit<RequestOptions, 'body'>) => request<T>('POST', path, { ...options, body: form }),
  /** GET a binary download (CSV/XLSX/PDF exports, label sheets). */
  download: (path: string, options?: Omit<RequestOptions, 'body' | 'responseType'>) => request<Blob>('GET', path, { ...options, responseType: 'blob' }),
};

/** A fresh key per user intent (one click of "Pay"), reused across automatic retries. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/** Trigger a browser download for a Blob (used with api.download). */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
